import type { EditorClip, EditorTextStyle } from '../models/editor-clip.model';
import {
  resolveTextStyleKeyframes,
  textStyleChannelHasKeyframes,
  type TextStyleKeyframeChannel,
  type TextStyleKeyframeFrame,
} from '../../../../core/render-runtime/shared/text-style-keyframes.util';

export interface ResolvedEditorTextStyle {
  color: string;
  textStyle: EditorTextStyle;
  frame: TextStyleKeyframeFrame;
}

export function editorTextStyleBaseFrame(
  color: string | null | undefined,
  style: EditorTextStyle | null | undefined,
): TextStyleKeyframeFrame {
  const value = style ?? {};
  return {
    color: color || '#ffffff',
    fontSize: value.fontSize ?? 48,
    blendOpacity: value.blendOpacity ?? 1,
    strokeColor: value.stroke || '#000000',
    strokeWidth: value.strokeWidth ?? 6,
    backgroundColor: value.background || '#000000',
    backgroundOpacity: value.backgroundOpacity ?? 0.65,
    backgroundRoundness: value.backgroundRoundness ?? 0.5,
    backgroundWidth: value.backgroundWidth ?? 1,
    backgroundHeight: value.backgroundHeight ?? 1,
    backgroundOffsetX: value.backgroundOffsetX ?? 0,
    backgroundOffsetY: value.backgroundOffsetY ?? 0,
    bubbleColor: value.bubble?.color || '#ffffff',
    bubbleOpacity: value.bubble?.opacity ?? 1,
    glowColor: value.glow?.color || color || '#ffffff',
    glowStrength: value.glow?.strength ?? 0.75,
    glowRange: value.glow?.range ?? 0.55,
    glowOffsetX: value.glow?.offsetX ?? 0,
    glowOffsetY: value.glow?.offsetY ?? 0,
    shadowColor: value.shadow?.color || '#000000',
    shadowOpacity: value.shadow?.opacity ?? 0.65,
    shadowBlur: value.shadow?.blur ?? 0.45,
    shadowDistance: value.shadow?.distance ?? 8,
    shadowAngle: value.shadow?.angle ?? 45,
    curve: value.curve ?? 0,
  };
}

export function resolveEditorTextStyleAt(
  color: string | null | undefined,
  style: EditorTextStyle | null | undefined,
  keyframes: EditorClip['textStyleKeyframes'],
  clipRelativeTime: number,
): ResolvedEditorTextStyle {
  const baseStyle = style ?? {};
  const frame = resolveTextStyleKeyframes(
    editorTextStyleBaseFrame(color, baseStyle),
    keyframes,
    clipRelativeTime,
  );
  const has = (channel: TextStyleKeyframeChannel): boolean => textStyleChannelHasKeyframes(keyframes, channel);
  const textStyle: EditorTextStyle = {
    ...baseStyle,
    fontSize: numberValue(frame.fontSize, baseStyle.fontSize ?? 48),
    blendOpacity: numberValue(frame.blendOpacity, baseStyle.blendOpacity ?? 1),
    curve: numberValue(frame.curve, baseStyle.curve ?? 0),
  };

  if (baseStyle.stroke != null || has('strokeColor') || has('strokeWidth')) {
    textStyle.stroke = stringValue(frame.strokeColor, baseStyle.stroke || '#000000');
    textStyle.strokeWidth = numberValue(frame.strokeWidth, baseStyle.strokeWidth ?? 6);
  }
  if (baseStyle.background != null || BACKGROUND_CHANNELS.some(has)) {
    textStyle.background = stringValue(frame.backgroundColor, baseStyle.background || '#000000');
    textStyle.backgroundOpacity = numberValue(frame.backgroundOpacity, baseStyle.backgroundOpacity ?? 0.65);
    textStyle.backgroundRoundness = numberValue(frame.backgroundRoundness, baseStyle.backgroundRoundness ?? 0.5);
    textStyle.backgroundWidth = numberValue(frame.backgroundWidth, baseStyle.backgroundWidth ?? 1);
    textStyle.backgroundHeight = numberValue(frame.backgroundHeight, baseStyle.backgroundHeight ?? 1);
    textStyle.backgroundOffsetX = numberValue(frame.backgroundOffsetX, baseStyle.backgroundOffsetX ?? 0);
    textStyle.backgroundOffsetY = numberValue(frame.backgroundOffsetY, baseStyle.backgroundOffsetY ?? 0);
  }
  if (baseStyle.bubble || has('bubbleColor') || has('bubbleOpacity')) {
    textStyle.bubble = {
      ...(baseStyle.bubble ?? { shape: 'rounded' }),
      color: stringValue(frame.bubbleColor, baseStyle.bubble?.color || '#ffffff'),
      opacity: numberValue(frame.bubbleOpacity, baseStyle.bubble?.opacity ?? 1),
    };
  }
  if (baseStyle.glow || GLOW_CHANNELS.some(has)) {
    textStyle.glow = {
      color: stringValue(frame.glowColor, baseStyle.glow?.color || color || '#ffffff'),
      strength: numberValue(frame.glowStrength, baseStyle.glow?.strength ?? 0.75),
      range: numberValue(frame.glowRange, baseStyle.glow?.range ?? 0.55),
      offsetX: numberValue(frame.glowOffsetX, baseStyle.glow?.offsetX ?? 0),
      offsetY: numberValue(frame.glowOffsetY, baseStyle.glow?.offsetY ?? 0),
    };
  }
  if (baseStyle.shadow || SHADOW_CHANNELS.some(has)) {
    textStyle.shadow = {
      color: stringValue(frame.shadowColor, baseStyle.shadow?.color || '#000000'),
      opacity: numberValue(frame.shadowOpacity, baseStyle.shadow?.opacity ?? 0.65),
      blur: numberValue(frame.shadowBlur, baseStyle.shadow?.blur ?? 0.45),
      distance: numberValue(frame.shadowDistance, baseStyle.shadow?.distance ?? 8),
      angle: numberValue(frame.shadowAngle, baseStyle.shadow?.angle ?? 45),
    };
  }

  return {
    color: stringValue(frame.color, color || '#ffffff'),
    textStyle,
    frame,
  };
}

export const BACKGROUND_CHANNELS: readonly TextStyleKeyframeChannel[] = [
  'backgroundColor', 'backgroundOpacity', 'backgroundRoundness', 'backgroundWidth',
  'backgroundHeight', 'backgroundOffsetX', 'backgroundOffsetY',
];
export const GLOW_CHANNELS: readonly TextStyleKeyframeChannel[] = [
  'glowColor', 'glowStrength', 'glowRange', 'glowOffsetX', 'glowOffsetY',
];
export const SHADOW_CHANNELS: readonly TextStyleKeyframeChannel[] = [
  'shadowColor', 'shadowOpacity', 'shadowBlur', 'shadowDistance', 'shadowAngle',
];

export function textStyleChannelLabel(channel: TextStyleKeyframeChannel): string {
  return channel.replace(/([A-Z])/g, ' $1').replace(/^./, letter => letter.toUpperCase());
}

function numberValue(value: string | number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function stringValue(value: string | number | undefined, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}
